<template>
  <div>
    <div v-if="loading" class="loader">
      <b-spinner
        variant="primary"
        type="grow"
        :label="`${this.$t('reports.loadingLabel')}...`"
      ></b-spinner>
    </div>
    <div v-else-if="emptyState">
      <div class="text-center">
        <p>
          <font-awesome-icon
            :icon="['fas', 'folder-open']"
            style="margin-right: 20px; font-size: 35px;"
          />
        </p>
        <h5>
          {{ this.$t('reports.treatmentStatus.noDataLabel') }}
        </h5>
        <p>
          {{ this.$t('reports.answersLabel') }}
        </p>
      </div>
    </div>

    <div v-else>
      <div class="summary">
        <div class="item">
          <header>
            <h2>{{ this.$t('reports.treatmentStatus.answers') }}</h2>
          </header>
          <div class="description">
            <span>{{ summary.totalAnswers }}</span>
          </div>
        </div>

        <div class="item">
          <header>
            <h2>{{ this.$t('reports.treatmentStatus.answeredLabel') }}</h2>
          </header>
          <div class="description">
            <span style="color: #28a745;">{{
              calculateAvarage(summary.answered, summary.totalAnswers)
            }}</span>
            <span>{{ summary.answered }}</span>
          </div>
        </div>

        <div class="item">
          <header>
            <h2>{{ this.$t('reports.treatmentStatus.openedLabel') }}</h2>
          </header>
          <div class="description">
            <span style="color: #dc3545;">{{
              calculateAvarage(summary.opened, summary.totalAnswers)
            }}</span>
            <span>{{ summary.opened }}</span>
          </div>
        </div>

        <div class="item">
          <header>
            <h2>{{ this.$t('reports.treatmentStatus.staggeredLabel') }}</h2>
          </header>
          <div class="description">
            <span style="color: #ffc107;">{{
              calculateAvarage(summary.staggered, summary.totalAnswers)
            }}</span>
            <span>{{ summary.staggered }}</span>
          </div>
        </div>

        <div class="item">
          <header>
            <h2>{{ this.$t('reports.treatmentStatus.analysedLabel') }}</h2>
          </header>
          <div class="description">
            <span style="color: #72bcd4;">{{
              calculateAvarage(summary.analysis, summary.totalAnswers)
            }}</span>
            <span>{{ summary.analysis }}</span>
          </div>
        </div>

        <div class="item">
          <header>
            <h2>{{this.$t('reports.treatmentStatus.others') }}</h2>
          </header>
          <div class="description">
            <span style="color: #72bcd4;">{{
              calculateAvarage(summary.other, summary.totalAnswers)
            }}</span>
            <span>{{ summary.other }}</span>
          </div>
        </div>
      </div>

      <div style="display: flex; padding-left: 15px;">
        <div class="row mt-4">
          <div style="width: 430px;">
            <b-form-select
              @change="loadChart"
              v-model="customerGroupSelected"
              :options="customerGroup"
              style="margin-left: 5px;"
            ></b-form-select>
          </div>
        </div>

        <div class="mt-4 cards" style="text-align: left; display: flex;">
          <div class="icons-grid">
            <i
              title="Barras"
              class="fas fa-chart-bar"
              @click="switchChart('barChart')"
            ></i>
          </div>

          <div class="icons-grid">
            <i
              title="Lista"
              class="fas fa-list"
              @click="switchChart('list')"
            ></i>
          </div>
        </div>
      </div>

      <div v-if="listing" class="px-3 mt-4">
        <b-table
          :responsive="true"
          :items="itemsMultiple"
          :fields="fieldsMultiple"
        ></b-table>
      </div>
      <highcharts v-if="barChart" :options="chartOptions" class="mt-2" />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import api from '../../../../services/api';

export default {
  name: 'TreatmentStatusReport',

  data: () => ({
    loading: false,
    emptyState: false,
    userAuth: null,
    channel: null,
    status: null,
    summary: {
      email: {
        totalAnswers: 0,
        answered: 0,
        opened: 0,
        staggered: 0,
        analysis: 0,
        other: 0,
      },
    },
    customerGroupSelected: null,
    customerGroup: [],
    chartOptions: {},
    barChart: true,
    listing: false,
    fieldsMultiple: [],
  }),

  computed: {
    ...mapState('authentication', ['userProfile']),
    ...mapState('actions', ['myActionId', 'actionGroupData', 'myActionsIds']),
    ...mapState('calendar', ['calendarDates']),
    ...mapState('companies', ['myMetric']),
    ...mapState('branches', ['myIh1', 'myIh2', 'myHierarchies1', 'myHierarchies2']),
    ...mapState('indicators', ['indicatorName', 'indicatorData']),
  },

  watch: {
    actionGroupData: function watchActionGroup() {
      this.loadChart();
    },

    myActionId: function watchmyActionId() {
      this.loadChart();
    },

    myActionsIds: function watchmyActionsIds() {
      this.loadChart();
    },

    calendarDates: function watchCalendarDates() {
      this.loadChart();
    },

    myIh1: function watchMyIh1() {
      this.loadChart();
    },

    myHierarchies1: function watchMyHierarchies1() {
      this.loadChart();
    },

    myHierarchies2: function watchMyHierarchies2() {
      this.loadChart();
    },

    myIh2: function watchMyIh2() {
      this.loadChart();
    },

    myMetric: function watchMyMetric() {
      this.loadChart();
      this.changeLabel();
    },

    'indicatorData.value': function watchIndicatorDataValue() {
      this.loadChart();
    },
    'indicatorData.period': async function watchIndicatorPeriod() {
      await this.loadChart();
    },
  },

  methods: {
    async switchChart(value) {
      if (value === 'barChart') {
        this.barChart = true;
        this.listing = false;
      } else {
        this.barChart = false;
        this.listing = true;
      }
    },

    async loadTable(data) {
      const headers = data.headers;
      const items = data.items;
      this.fieldsMultiple = [];

      let i = 0;
      // eslint-disable-next-line
      for (let property in items[0]){
        const prop = property;
        this.fieldsMultiple.push({
          key: prop,
          label: headers[i],
          sortable: false,
          class: i === 0 ? 'w500' : 'w150 text-center',
        });

        i += 1;
      }

      this.itemsMultiple = items;
    },

    async loadChart() {
      this.loading = true;
      this.emptyState = false;

      const response = await api.get('/reports/treatment-status', {
        headers: {
          'x-access-token': this.userAuth.token,
        },
        params: {
          actionId: this.myActionsIds.length === 0 ? null : this.myActionsIds.join(','),
          startDate: this.calendarDates.start,
          endDate: this.calendarDates.end,
          ih1: this.myHierarchies1.length === 0 ? null : this.myHierarchies1.join(','),
          ih2: this.myHierarchies2.length === 0 ? null : this.myHierarchies2.join(','),
          metric: this.myMetric,
          groupId: this.actionGroupData._id,
          indicator: this.indicatorName,
          indicatorValue: this.indicatorData.value,
          indicatorRange: this.indicatorData.range,
          customerGroup: this.customerGroupSelected,
        },
      });

      this.chartOptions.xAxis.categories = response.data.chartData.categories;

      this.chartOptions.series[0].data =
        response.data.chartData.status.answered;

      this.chartOptions.series[1].data = response.data.chartData.status.opened;

      this.chartOptions.series[2].data =
        response.data.chartData.status.staggered;

      this.chartOptions.series[3].data =
        response.data.chartData.status.analysis;

      this.chartOptions.series[4].data = response.data.chartData.status.other;

      if (this.chartOptions.xAxis.categories.length === 0) {
        this.emptyState = true;
      }

      this.summary.totalAnswers = response.data.summary.totalAnswers;
      this.summary.answered = response.data.summary.answered;
      this.summary.opened = response.data.summary.opened;
      this.summary.staggered = response.data.summary.staggered;
      this.summary.analysis = response.data.summary.analysis;
      this.summary.other = response.data.summary.other;

      await this.loadTable(response.data.table);

      this.loading = false;
    },

    calculateAvarage(dividend, divider) {
      if (!dividend || !divider) {
        return '0%';
      }

      return `${Number(((dividend / divider) * 100).toFixed(2))}%`;
    },

    changeLabel() {
      let promotersText = '';
      let passivesText = '';
      let detractorsText = '';

      if (this.myMetric.indexOf('nps') >= 0) {
        promotersText = this.$t('reports.treatmentStatus.promoterLabel');
        passivesText = this.$t('reports.treatmentStatus.neutralLabel');
        detractorsText = this.$t('reports.treatmentStatus.detractorLabel');
      } else if (
        this.myMetric.indexOf('ces') >= 0 ||
        this.myMetric.indexOf('stars') >= 0
      ) {
        promotersText = '5-4';
        passivesText = '3';
        detractorsText = '2-1';
      } else if (this.myMetric.indexOf('csat') >= 0) {
        promotersText = '5-4';
        passivesText = '3';
        detractorsText = '2-1';
      }

      this.customerGroup = [
        { value: null, text: this.$t('reports.treatmentStatus.allSelected') },
        { value: 'promoters', text: promotersText },
        { value: 'passives', text: passivesText },
        { value: 'detractors', text: detractorsText },
      ];
    },
  },

  created() {
    this.userAuth = JSON.parse(localStorage.getItem('_indecx'));

    this.changeLabel();

    this.chartOptions = {
      chart: {
        type: 'column',
      },
      title: {
        text: this.$t('reports.treatmentStatus.chartTitle'),
      },
      xAxis: {
        categories: [],
      },
      yAxis: {
        min: 0,
        title: {
          text: '',
        },
      },
      tooltip: {
        pointFormat:
          '<span style="color:{series.color}">{series.name}</span>: <b>{point.y}</b> ({point.percentage:.0f}%)<br/>',
        shared: true,
      },
      plotOptions: {
        column: {
          stacking: 'percent',
          dataLabels: {
            enabled: true,
          },
        },
      },
      series: [
        {
          name: this.$t('reports.treatmentStatus.answeredLabel'),
          data: [],
          color: '#28a745',
        },
        {
          name: this.$t('reports.treatmentStatus.openedLabel'),
          data: [],
          color: '#dc3545',
        },
        {
          name: this.$t('reports.treatmentStatus.staggeredLabel'),
          data: [],
          color: '#ffc107',
        },
        {
          name: this.$t('reports.treatmentStatus.analysedLabel'),
          data: [],
          color: '#72bcd4',
        },
        {
          name: this.$t('reports.treatmentStatus.others'),
          data: [],
          color: '#4ce595',
        },
      ],
    };
  },

  async mounted() {
    this.loading = true;

    await this.loadChart();

    this.loading = false;
  },
};
</script>

<style lang="scss" scoped>
@import '@/themes/default/variables.scss';

.cards {
  border-radius: 4px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom-color: none;
  position: relative;
  background-color: #fff;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  color: #5f6368;
  padding: 0 8px;
  margin-left: 35px;
  > div {
    border-right: 1px solid $gray-light;
    padding: 8px 16px 8px 8px;
  }
  .icons-grid {
    padding-left: 16px;
    > i {
      opacity: 0.7;
      transition: all 0.2s linear;
      cursor: pointer;
      &:hover {
        opacity: 1;
      }
    }
  }
}

.w500 {
  width: 150px;
}
.w150 {
  width: 150px;
}

.summary {
  display: flex;
  flex-direction: row;
  margin-left: 5px;

  .item {
    display: flex;
    flex-direction: column;
    padding: 5px 8px;
    margin-right: 10px;
    min-height: 79px;
    max-height: 79px;
    min-width: 115px;
    max-width: 115px;
    -webkit-box-shadow: 0px 0px 9px -6px #000000;
    box-shadow: 0px 0px 9px -6px #000000;
    transition: 0.3s;

    &:hover {
      -webkit-box-shadow: 0px 0px 12px -6px #000000;
      box-shadow: 0px 0px 12px -6px #000000;
    }

    header {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-transform: uppercase;

      h2 {
        font-size: 11px;
      }
    }

    .description {
      display: flex;
      flex-direction: column;
      flex: 1;
      justify-content: center;
      align-items: center;

      span {
        font-size: 22px;
        font-weight: lighter;

        & + span {
          margin-top: -3px;
          font-size: 12px;
        }
      }
    }

    &:last-of-type {
      margin-right: 30px;
    }
  }
}

.search-invites {
  display: flex;
  flex-direction: row;
  input {
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;
  }
  button {
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
  }
}

.loader {
  width: 100%;
  height: 350px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.detractor-review {
  display: flex;
  justify-content: center;
  align-items: center;

  background: red;
  color: white;
  height: 30px;
  width: 30px;
  border-radius: 50%;
}

.pagination-area-status-invites >>> a {
  position: relative;
  display: block;
  padding: 0.5rem 0.75rem;
  margin-left: -1px;
  line-height: 1.25;
  color: #4ce595;
  background-color: #fff;
  border: 1px solid #dee2e6;
}
</style>

<style>
.w500 {
  width: 150px;
}
.w150 {
  width: 150px;
}

.pagination-area-status-invites .active a {
  text-decoration: none;
  z-index: 1;
  color: #fff;
  background-color: #4ce595;
  border-color: #4ce595;
}

.pagination-area-status-invites a {
  outline: none;
}
</style>



// WEBPACK FOOTER //
// src/components/molecules/highcharts/treatment-status/index.vue